/**
 * Companies repository — data access for the platform `companies` table + its
 * four control-plane RPCs. Great Migration slice a14 (N-3).
 *
 * This is the SINGLE home for these calls; hooks/pages call the repository, never
 * `supabase.rpc()` / `supabase.from('companies')` directly.
 *
 * TRANSPORT FLIP STATUS (slice a14 / N-3, Step 3 — all gated on `API_URL`):
 *   - `getMainCompanyId()` → `GET /companies/main` (Nest-first, RPC fallback).
 *     Flipped first because `get_main_company_id()` is a platform-global
 *     singleton (`companies.is_main`) with NO `auth.uid()` dependency.
 *   - `superAdminCompaniesWithCounts()` → `GET /companies/super-admin/list`
 *     (read: standard any-error fallback to the RPC).
 *   - `setMainCompany()` / `setCompanyActive()` → the super-admin write
 *     endpoints. WRITE-PATH RULE (§2.4): fall back to the RPC ONLY on genuine
 *     transport failures (network/timeout/5xx — both RPCs are idempotent, so a
 *     re-issue is safe); a definitive 4xx is the server's answer and is
 *     RETHROWN (a 403 falling back would mask a broken guard and double-run a
 *     control-plane write). Server-side these endpoints still invoke the RPCs
 *     over the caller's token (they assert `auth.uid()` internally, and
 *     set-active sweeps auth sessions) — the flip moves the TRANSPORT, not the
 *     authz.
 *   - `getById()` / `updateCompany()` → `GET|PATCH /companies/current`
 *     (token-scoped; the Supabase table read/write remains the fallback).
 *   - `listActivity()` — Supabase-internal permanently from this slice's
 *     perspective (session-boot path; D3/auth-track owns its future).
 *
 * RPCs owned here:
 *   - get_main_company_id()                     platform-global main-company read (any authed user)
 *   - set_main_company(p_company_id)            super_admin-only main-company promote (atomic)
 *   - rpc_super_admin_companies_with_counts()   super_admin-only cross-tenant list + live counts
 *   - rpc_set_company_active(id, is_active)     super_admin-only activate/deactivate (+ session sweep)
 *
 * NOTE: `get_main_company_id` returns the platform-wide singleton (companies.is_main),
 * NOT a per-user value — it is deliberately NOT company-scoped.
 */
import { supabase } from "@/integrations/supabase/client";
import { serializeError } from "@/lib/sentry";
import type { Database, Json } from "@/integrations/supabase/types";
import type { PostgrestError } from "@supabase/supabase-js";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";
import {
  isDefinitiveNotFound,
  reportNestFallback,
  reportNestFallbackOrRethrowDefinitive,
} from "@/lib/nestFallback";
import {
  mainCompanyResponseSchema,
  superAdminCompaniesResponseSchema,
  setMainCompanyResponseSchema,
  setCompanyActiveResponseSchema,
  companySettingsSchema,
  type CompanySettingsWire,
} from "@dallali/shared";

// Authenticated Nest request bound to the "companies" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
const nestRequest = makeNestRequest("companies");

export type SuperAdminCompanyRow =
  Database["public"]["Functions"]["rpc_super_admin_companies_with_counts"]["Returns"][number];

/** Envelope returned by rpc_set_company_active ({ success, error_code?, ... }). */
export type SetCompanyActiveResult =
  Database["public"]["Functions"]["rpc_set_company_active"]["Returns"];

/**
 * The company row shape consumed by settings + the FE-tail narrow reads
 * (useTenantData / ContractDetails / useFinance ride this later) — the
 * snake_case wire of `GET /companies/current`, a strict subset of the
 * Supabase `companies` Row.
 */
export type CompanyRow = CompanySettingsWire;

export const companiesRepository = {
  /**
   * The platform-wide "main" company id (companies.is_main = true), or null when
   * none is designated. Any authenticated user may read it. No caching decision
   * here — callers (useMainCompanyId) own their query freshness.
   *
   * FLIPPED (slice a14 / N-3): Nest-first via the guarded `GET /companies/main`
   * (gated on `API_URL` / VITE_API_URL), parsing the response against the shared
   * `mainCompanyResponseSchema` so any drift throws into the fallback rather than
   * corrupting the platform-wide tenancy anchor. On ANY Nest error (config, auth
   * boot-race, timeout, 5xx, parse) it reports the fallback and reads via the
   * `get_main_company_id()` RPC — behavior-identical to pre-flip and the
   * local-dev path (no API configured). Fail-closed: if the Supabase fallback
   * also errors it throws, and `useMainCompanyId` surfaces null (never a wrong
   * company). This is the tenancy hot path — a wrong value breaks every
   * company-scoped query, so the parse gate + loud fallback are deliberate.
   */
  async getMainCompanyId(): Promise<string | null> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(API_ROUTES.companies.main());
        return mainCompanyResponseSchema.parse(res).mainCompanyId;
      } catch (err) {
        reportNestFallback("companies.getMainCompanyId", err);
      }
    }
    const { data, error } = await supabase.rpc("get_main_company_id");
    if (error) throw new Error(error.message);
    return data ?? null;
  },

  /**
   * Cross-tenant company list with LIVE per-company counts. super_admin-only —
   * the endpoint is JwtSuperAdminGuard-gated and the RPC raises 42501 for any
   * other caller (defence-in-depth on top of the page's route guard).
   *
   * FLIPPED: Nest-first via `GET /companies/super-admin/list` (rows verbatim,
   * parsed against the shared row schema); standard any-error fallback to the
   * RPC (reads are side-effect-free).
   */
  async superAdminCompaniesWithCounts(): Promise<SuperAdminCompanyRow[]> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(
          API_ROUTES.companies.superAdminList(),
        );
        return superAdminCompaniesResponseSchema.parse(
          res,
        ) as SuperAdminCompanyRow[];
      } catch (err) {
        reportNestFallback("companies.superAdminCompaniesWithCounts", err);
      }
    }
    const { data, error } = await supabase.rpc(
      "rpc_super_admin_companies_with_counts",
    );
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Promote a company to platform-main (clears any existing main atomically).
   * super_admin-only; the RPC raises 42501 otherwise.
   *
   * FLIPPED (write-path rule §2.4): Nest-first via
   * `POST /companies/super-admin/main`; transport failures fall back to the
   * RPC (idempotent), a definitive 4xx RETHROWS — never re-run the RPC on the
   * server's answer.
   */
  async setMainCompany(companyId: string): Promise<void> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(API_ROUTES.companies.setMain(), {
          method: "POST",
          body: JSON.stringify({ companyId }),
        });
        setMainCompanyResponseSchema.parse(res);
        return;
      } catch (err) {
        reportNestFallbackOrRethrowDefinitive("companies.setMainCompany", err);
      }
    }
    const { error } = await supabase.rpc("set_main_company", {
      p_company_id: companyId,
    });
    if (error) throw new Error(error.message);
  },

  /**
   * Toggle companies.is_active. super_admin-only. Returns the RPC envelope
   * verbatim ({ success, error_code? }) — the caller maps error_code
   * (self_lockout / forbidden / not_found) to a bilingual message, since the
   * RPC reports business failures in-band rather than by throwing.
   *
   * FLIPPED (write-path rule §2.4): Nest-first via
   * `PATCH /companies/super-admin/:id/active` — the endpoint returns the
   * envelope verbatim with HTTP 200 even for success:false (parsed against the
   * shared envelope schema). Transport failures fall back to the RPC
   * (idempotent); a definitive 4xx RETHROWS. The Nest path preserves the
   * session sweep — the endpoint invokes the RPC over the caller's token.
   */
  async setCompanyActive(
    companyId: string,
    isActive: boolean,
  ): Promise<SetCompanyActiveResult> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(
          API_ROUTES.companies.setActive(companyId),
          { method: "PATCH", body: JSON.stringify({ isActive }) },
        );
        return setCompanyActiveResponseSchema.parse(
          res,
        ) as SetCompanyActiveResult;
      } catch (err) {
        reportNestFallbackOrRethrowDefinitive("companies.setCompanyActive", err);
      }
    }
    const { data, error } = await supabase.rpc("rpc_set_company_active", {
      p_company_id: companyId,
      p_is_active: isActive,
    });
    if (error) {
      console.error("Error setting company active:", serializeError(error));
      throw error;
    }
    return data;
  },

  /**
   * A single company row (settings projection), or null when absent /
   * RLS-denied. Consolidates the `useCompanySettings` read; published for the
   * FE-tail wave (task #9) — narrow company reads (useTenantData /
   * ContractDetails / useFinance) ride this instead of minting their own.
   *
   * FLIPPED: Nest-first via the token-scoped `GET /companies/current` (the
   * server resolves the company from the JWT — `companyId` is NOT sent; the
   * caller passes it for the Supabase fallback + cache keys). A definitive
   * app-level 404 is the server's real answer — returned as `null` directly
   * (matching `.maybeSingle()`), no fallback re-ask, no telemetry (#3533);
   * every other error falls back with a `nest-fallback` report.
   *
   * @param companyId used ONLY by the Supabase fallback's `.eq("id", …)` and
   *   cache keys — the Nest path ignores it and returns the JWT-resolved
   *   company. Do NOT pass a foreign company id expecting a cross-company
   *   read; super-admin cross-tenant reads go through the super-admin methods.
   */
  async getById(companyId: string): Promise<CompanyRow | null> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(API_ROUTES.companies.current());
        return companySettingsSchema.parse(res);
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
        reportNestFallback("companies.getById", err);
      }
    }
    const { data, error } = await supabase
      .from("companies")
      .select("*")
      .eq("id", companyId)
      .maybeSingle();
    if (error) throw error;
    return (data as unknown as CompanyRow) ?? null;
  },

  /**
   * Update the company row (settings form). Preserves the legacy semantics:
   * `settings` is a whole-object REPLACE (no merge), and an update that
   * matches no row (absent / RLS-denied) throws the user-visible
   * "Company not found or access denied".
   *
   * FLIPPED (write-path rule §2.4): Nest-first via the settings-guarded
   * `PATCH /companies/current` (token-scoped, body = snake_case partial).
   * Transport failures fall back to the Supabase update (idempotent partial
   * replacement); a definitive 4xx RETHROWS (the server's bilingual
   * companies-00x body reaches the toast).
   */
  async updateCompany(
    companyId: string,
    updates: Partial<CompanyRow>,
  ): Promise<CompanyRow> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(API_ROUTES.companies.current(), {
          method: "PATCH",
          body: JSON.stringify(updates),
        });
        return companySettingsSchema.parse(res);
      } catch (err) {
        reportNestFallbackOrRethrowDefinitive("companies.updateCompany", err);
      }
    }
    // Cast `settings` for the generated Update type (JSONB column) — the wire
    // value is already the whole replacement object.
    const { settings: settingsJson, ...rest } = updates;
    const payload =
      settingsJson !== undefined
        ? { ...rest, settings: settingsJson as unknown as Json }
        : rest;
    const { data, error } = await supabase
      .from("companies")
      .update(payload)
      .eq("id", companyId)
      .select()
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error("Company not found or access denied");
    return data as unknown as CompanyRow;
  },

  /**
   * Persist the corporate unified number onto the company row (ads-wizard
   * REGA step pre-fill — fire-and-forget from the caller). Supabase-only
   * (narrow single-column write; the settings-form path is updateCompany).
   * Returns the error instead of throwing — the wizard toasts + Sentry-logs
   * without blocking license validation.
   */
  async setUnifiedNumber(
    companyId: string,
    unifiedNumber: string,
  ): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase
      .from("companies")
      .update({ unified_number: unifiedNumber })
      .eq("id", companyId);
    return { error };
  },

  /**
   * Activation flags for a set of companies (session-boot deactivation check).
   * Supabase-internal permanently from this slice's perspective — the boot
   * path is D3/auth-track-owned. NEVER throws: the boot caller ignored errors
   * by design (an activity-read blip must not lock users out), so an error
   * resolves to [] exactly like the pre-consolidation destructure did.
   */
  async listActivity(
    companyIds: string[],
  ): Promise<Array<{ id: string; is_active: boolean | null }>> {
    const { data } = await supabase
      .from("companies")
      .select("id, is_active")
      .in("id", companyIds);
    return data ?? [];
  },

  /**
   * Management-fee settings for the finance stats / owner-payouts math.
   * KEPT on Supabase — narrow finance sub-read. NEVER throws: both
   * pre-consolidation sites destructured `{ data }` only and defaulted to
   * percentage/10 when the read failed, so an error resolves to null.
   */
  async getManagementFeeSettings(companyId: string): Promise<{
    management_fee_type: string | null;
    management_fee_value: number | null;
  } | null> {
    const { data } = await supabase
      .from("companies")
      .select("management_fee_type, management_fee_value")
      .eq("id", companyId)
      .maybeSingle();
    return data;
  },

  /**
   * Company identity/branding columns for the contract print view. KEPT on
   * Supabase — narrow print sub-read; throws on error.
   */
  async getPrintHeader(companyId: string): Promise<{
    name: string;
    name_en: string | null;
    vat_number: string | null;
    cr_number: string | null;
    logo_url: string | null;
  } | null> {
    const { data, error } = await supabase
      .from("companies")
      .select("name, name_en, vat_number, cr_number, logo_url")
      .eq("id", companyId)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  /**
   * VAT rate for gross-fee scaling in the finance summary. KEPT on Supabase —
   * narrow finance sub-read. NEVER throws: the pre-consolidation site
   * destructured `{ data }` only (a failed read falls back to the no-VAT
   * scaling), so an error resolves to null.
   */
  async getVatRate(
    companyId: string,
  ): Promise<{ vat_rate: number | null } | null> {
    const { data } = await supabase
      .from("companies")
      .select("vat_rate")
      .eq("id", companyId)
      .maybeSingle();
    return data;
  },
};
